import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import PageHeader from '@/components/Layout/PageHeader';
import { createScoreboardState } from '@/components/scoreboard/engine';

interface ScoreboardSummary {
  id: string;
  title: string;
  home_team: string | null;
  away_team: string | null;
  updated_at: string;
}

export default function ScoreboardListPage() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState<ScoreboardSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [eventName, setEventName] = useState('');
  const [teamAName, setTeamAName] = useState('');
  const [teamBName, setTeamBName] = useState('');
  const [halfMinutes, setHalfMinutes] = useState(30);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get<{ matches: ScoreboardSummary[] }>('/scoreboards')
      .then((res) => {
        setMatches(res.data.matches);
        setError(null);
      })
      .catch(() => setError('無法載入比分牌'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      const state = createScoreboardState({ eventName, teamAName, teamBName, halfMinutes });
      const res = await api.post<{ match: { id: string } }>('/scoreboards', { state });
      setOpen(false);
      navigate(`/scoreboard/${res.data.match.id}`);
    } catch {
      setError('無法建立比分牌');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('確定刪除此比分牌？')) return;
    await api.delete(`/scoreboards/${id}`);
    load();
  };

  return (
    <Box>
      <PageHeader
        icon={<ScoreboardIcon />}
        title="賽事比分牌"
        subtitle="手球計時、比分、罰牌與罰時，可投放到第二螢幕"
        actions={
          <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            新比賽
          </Button>
        }
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Typography color="text.secondary">載入中…</Typography>
      ) : matches.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 3, py: 6, textAlign: 'center' }}>
          <Typography fontWeight={700}>尚未建立比分牌</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            建立一場比賽後即可操作計時與比分，並開啟 LED 顯示畫面
          </Typography>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {matches.map((m) => (
            <Card key={m.id} variant="outlined" sx={{ borderRadius: 3 }}>
              <CardActionArea onClick={() => navigate(`/scoreboard/${m.id}`)}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={800} noWrap>
                        {m.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        更新 {new Date(m.updated_at).toLocaleString('zh-HK')}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(m.id);
                      }}
                    >
                      刪除
                    </Button>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>新比賽</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="賽事名稱" value={eventName} onChange={(e) => setEventName(e.target.value)} fullWidth />
            <TextField label="A隊" value={teamAName} onChange={(e) => setTeamAName(e.target.value)} fullWidth />
            <TextField label="B隊" value={teamBName} onChange={(e) => setTeamBName(e.target.value)} fullWidth />
            <TextField
              label="半場分鐘"
              type="number"
              value={halfMinutes}
              onChange={(e) => setHalfMinutes(Number(e.target.value) || 30)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>取消</Button>
          <Button variant="contained" color="secondary" disabled={saving} onClick={create}>
            建立
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
